import { Slot, Slottable } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../lib/cn";
import { Spinner } from "./spinner";

const buttonVariants = cva(
	"flex items-center justify-center font-medium enabled:cursor-pointer transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&>svg]:mr-1.5 [&>svg]:opacity-60 [&>svg+svg]:hidden",
	{
		variants: {
			variant: {
				primary:
					"bg-primary text-primary-foreground hover:bg-primary/80",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80",
				outline:
					"border border-secondary/15 bg-transparent text-secondary hover:bg-secondary/10",
				ghost: "text-foreground hover:bg-foreground/10 hover:text-foreground",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/80",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				sm: "h-6 rounded-full px-3 text-xs",
				md: "h-9 rounded-full px-4 text-sm",
				lg: "h-12 rounded-full px-6 text-base",
				icon: "size-8 rounded-full [&>svg]:m-0 [&>svg]:opacity-100",
			},
		},
		defaultVariants: {
			variant: "secondary",
			size: "md",
		},
	},
);

export type ButtonProps = {
	asChild?: boolean;
	loading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
	VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
	{
		className,
		children,
		variant,
		size,
		asChild = false,
		loading,
		disabled,
		...props
	},
	ref,
) {
	const Comp = asChild ? Slot : "button";
	return (
		<Comp
			ref={ref}
			className={cn(buttonVariants({ variant, size, className }))}
			disabled={disabled || loading}
			{...props}
		>
			{loading && <Spinner className="mr-1.5 size-4 text-inherit" />}
			<Slottable>{children}</Slottable>
		</Comp>
	);
});

Button.displayName = "Button";

export { Button, buttonVariants };
